import { GeoJSONFeature } from 'maplibre-gl';
import { polygon as turfPolygon, booleanWithin } from '@turf/turf';

import { geojsonTypes, updateActions, events, modes } from './utils/constants';
import { drawModes, drawState } from '../../Types';

type CreateHoleModeType = drawModes & {
  applyHole: (state: HoleSetupState, holeFeature: GeoJSON.Feature) => void;
  startSubtypeDraw: (
    state: HoleSetupState,
    callback: (holeFeature: GeoJSON.Feature) => void,
  ) => void;
};

const CreateHoleMode = <CreateHoleModeType>{};

type HoleSetupState = drawState & {
  targetId: string;
  targetSnapshot: GeoJSON.Feature;
  startedDraw?: boolean;
  drawSubtype?: string;
  awaitingHoleSketch?: boolean;
};

const HOLE_DRAW_MODES: Record<string, string> = {
  rectangle: 'draw_rectangle',
  circle: 'draw_circle',
  freehand: 'draw_polygon_freehand',
};

function normalizeHoleDrawSubtype(subtype?: string): string {
  if (subtype && subtype in HOLE_DRAW_MODES) return subtype;
  return 'polygon';
}

function normalizeHoleTargetFeature(
  feature: GeoJSON.Feature | null | undefined
): GeoJSON.Feature | null {
  const geom = feature?.geometry;
  if (!geom) return null;
  if (geom.type === geojsonTypes.POLYGON) {
    return feature as GeoJSON.Feature;
  }
  if (geom.type === geojsonTypes.MULTI_POLYGON) {
    const coords = (geom as GeoJSON.MultiPolygon).coordinates;
    if (!Array.isArray(coords) || coords.length !== 1) return null;
    return {
      ...feature,
      geometry: {
        type: geojsonTypes.POLYGON,
        coordinates: JSON.parse(JSON.stringify(coords[0])),
      },
    } as GeoJSON.Feature;
  }
  return null;
}

CreateHoleMode.onSetup = function (opts: { featureId?: string; drawSubtype?: string }) {
  let targetGeo: GeoJSON.Feature | null = null;
  let targetId: string | undefined;

  if (opts?.featureId != null) {
    targetId = String(opts.featureId);
    const feature = this.getFeature(targetId);
    if (feature) targetGeo = normalizeHoleTargetFeature(feature.toGeoJSON());
  } else {
    const selected = this.getSelected()
      .map((f: any) => normalizeHoleTargetFeature(f.toGeoJSON()))
      .filter((f: GeoJSON.Feature | null): f is GeoJSON.Feature => Boolean(f));
    if (selected.length === 1) {
      targetGeo = selected[0];
      targetId = String(selected[0].id);
    }
  }

  if (!targetGeo || targetGeo.geometry?.type !== geojsonTypes.POLYGON || !targetId) {
    throw new Error('Select a single polygon feature to create a hole.');
  }

  return {
    targetId,
    targetSnapshot: JSON.parse(JSON.stringify(targetGeo)),
    startedDraw: false,
    drawSubtype: normalizeHoleDrawSubtype(opts?.drawSubtype),
    awaitingHoleSketch: false,
  } as HoleSetupState;
};

CreateHoleMode.startSubtypeDraw = function (
  state: HoleSetupState,
  callback: (holeFeature: GeoJSON.Feature) => void
) {
  const subtype = state.drawSubtype ?? 'polygon';

  const keepVisibleFeatureIds = [state.targetId];

  if (subtype === 'polygon') {
    this.changeMode('passing_mode_polygon', {
      callback,
      keepVisibleFeatureIds,
    });
    return;
  }

  const drawMode = HOLE_DRAW_MODES[subtype];
  if (!drawMode) {
    this.changeMode('passing_mode_polygon', {
      callback,
      keepVisibleFeatureIds,
    });
    return;
  }

  const map = this.map;
  const ctx = this;
  state.awaitingHoleSketch = true;

  const cleanup = () => {
    map.off?.('draw.create', onDrawCreate);
  };

  const onDrawCreate = (e: { features?: GeoJSON.Feature[] }) => {
    if (!state.awaitingHoleSketch) return;
    state.awaitingHoleSketch = false;
    cleanup();

    const features = e?.features ?? [];
    const holeFeature = features.find(
      (f) =>
        String(f?.id) !== state.targetId &&
        f?.geometry?.type === geojsonTypes.POLYGON
    );

    if (!holeFeature?.geometry) {
      try {
        ctx.changeMode(modes.SIMPLE_SELECT, { featureIds: [state.targetId] });
      } catch {
        /* ignore */
      }
      try {
        map.fire('draw.createhole.failed', { reason: 'invalidHole' });
      } catch {
        /* ignore */
      }
      return;
    }

    const holeGeo = JSON.parse(JSON.stringify(holeFeature)) as GeoJSON.Feature;
    try {
      ctx.deleteFeature([String(holeFeature.id)], { silent: true });
    } catch {
      /* ignore */
    }
    callback(holeGeo);
  };

  map.on?.('draw.create', onDrawCreate);
  this.changeMode(drawMode, { keepVisibleFeatureIds });
};

CreateHoleMode.toDisplayFeatures = function (
  state: HoleSetupState,
  geojson: GeoJSONFeature,
  display: (feature: GeoJSONFeature) => void
) {
  display(geojson);

  if (state.startedDraw) return;
  state.startedDraw = true;

  const callback = (holeFeature: GeoJSON.Feature) => {
    CreateHoleMode.applyHole.call(this, state, holeFeature);
  };

  CreateHoleMode.startSubtypeDraw.call(this, state, callback);
};

CreateHoleMode.applyHole = function (
  state: HoleSetupState,
  holeFeature: GeoJSON.Feature
) {
  const finish = (reason?: string) => {
    if (reason) {
      try {
        this.map.fire('draw.createhole.failed', { reason });
      } catch {
        /* ignore */
      }
    }
    try {
      this.changeMode(modes.SIMPLE_SELECT, { featureIds: [state.targetId] });
    } catch {
      /* ignore */
    }
  };

  const target = this.getFeature(state.targetId);
  if (!target) {
    finish('noTarget');
    return;
  }

  const holeGeom = holeFeature?.geometry;
  if (holeGeom?.type !== geojsonTypes.POLYGON) {
    finish('invalidHole');
    return;
  }

  const holePolygon = holeGeom as GeoJSON.Polygon;
  const holeRing = holePolygon.coordinates?.[0];
  if (!Array.isArray(holeRing) || holeRing.length < 4) {
    finish('invalidHole');
    return;
  }

  const targetPolygon = state.targetSnapshot.geometry as
    | GeoJSON.Polygon
    | undefined;
  const exteriorCoords = targetPolygon?.coordinates;
  if (
    !Array.isArray(exteriorCoords) ||
    !isHoleInsidePolygon(exteriorCoords, holeFeature)
  ) {
    finish('notInside');
    return;
  }

  const newCoords = [
    ...exteriorCoords.map((ring: number[][]) => [...ring]),
    [...holeRing],
  ];

  try {
    target.incomingCoords(newCoords);
    const updated = target.toGeoJSON();
    this.map.fire(events.UPDATE, {
      action: updateActions.CHANGE_COORDINATES,
      features: [updated],
    });
    this.map.fire('draw.createhole', { features: [updated] });
  } catch {
    finish('updateFailed');
    return;
  }

  try {
    this.changeMode(modes.SIMPLE_SELECT, { featureIds: [state.targetId] });
  } catch {
    /* ignore */
  }
};

/** Hole must lie entirely within the target polygon's outer ring. */
function isHoleInsidePolygon(
  polygonCoords: number[][][],
  holeFeature: GeoJSON.Feature
): boolean {
  try {
    const outerRing = polygonCoords[0];
    if (!Array.isArray(outerRing) || outerRing.length < 4) return false;
    const host = turfPolygon([outerRing]);
    return booleanWithin(holeFeature as GeoJSON.Feature<GeoJSON.Polygon>, host);
  } catch {
    return false;
  }
}

export default CreateHoleMode;
